import { parseHostnameFromEnv } from './brandFromHost'

/** Lowercase hostname without port or leading www. */
export function normalizeManagedHostname(hostname: string): string {
  return hostname
    .toLowerCase()
    .replace(/:\d+$/, '')
    .replace(/^www\./, '')
}

export function isAiClaimsHostname(hostname: string): boolean {
  const host = normalizeManagedHostname(hostname)
  return (
    host === 'aiclaims.co.uk' ||
    host.endsWith('.aiclaims.co.uk') ||
    host === 'aiclaims.local' ||
    host.endsWith('.aiclaims.local')
  )
}

/** Public marketing site (aiclaims.co.uk, staging.aiclaims.co.uk, …). */
export function isAiClaimsRootHostname(hostname: string): boolean {
  const host = normalizeManagedHostname(hostname)
  if (!isAiClaimsHostname(host)) {
    return false
  }
  return !host.startsWith('portal.')
}

/** Authenticated portal (portal.aiclaims.co.uk, portal.staging.aiclaims.co.uk, …). */
export function isAiClaimsPortalHostname(hostname: string): boolean {
  const host = normalizeManagedHostname(hostname)
  return isAiClaimsHostname(host) && host.startsWith('portal.')
}

export function isWyseaiHostname(hostname: string): boolean {
  const host = normalizeManagedHostname(hostname)
  return (
    host === 'wyseai.co.uk' ||
    host.endsWith('.wyseai.co.uk') ||
    host === 'wyseai.local' ||
    host.endsWith('.wyseai.local')
  )
}

/** WyseAi apex for the same environment as the current request
 * (wyseai.co.uk, staging.wyseai.co.uk, rosie.dev.wyseai.co.uk, …). */
export function inferWyseaiHostname(currentHost: string): string {
  const host = normalizeManagedHostname(currentHost)
  const derived = deriveWyseaiHostnameFromPeer(host)
  if (derived) {
    return derived
  }
  return (
    parseHostnameFromEnv(process.env.NEXT_PUBLIC_WYSEAI_ROOT_HOST) ||
    (isStagingManagedHost(host) ? 'staging.wyseai.co.uk' : 'wyseai.co.uk')
  )
}

function deriveWyseaiHostnameFromPeer(host: string): string | null {
  if (isWyseaiHostname(host)) {
    return host.replace(/^portal\./, '')
  }

  let apex = host
  for (const prefix of ['portal.', 'admin.', 'wyseai.'] as const) {
    if (apex.startsWith(prefix)) {
      apex = apex.slice(prefix.length)
      break
    }
  }

  const pairs: Array<[string, string]> = [
    ['aiclaims.co.uk', 'wyseai.co.uk'],
    ['wysehub.co.uk', 'wyseai.co.uk'],
    ['aiclaims.local', 'wyseai.local'],
    ['wysehub.local', 'wyseai.local'],
  ]
  for (const [oldSuffix, next] of pairs) {
    if (apex === oldSuffix) {
      return next
    }
    if (apex.endsWith(`.${oldSuffix}`)) {
      return `${apex.slice(0, -oldSuffix.length)}${next}`
    }
  }
  return null
}

export function isWysehubHostname(hostname: string): boolean {
  const host = normalizeManagedHostname(hostname)
  return (
    host === 'wysehub.co.uk' ||
    host.endsWith('.wysehub.co.uk') ||
    host === 'wysehub.local' ||
    host.endsWith('.wysehub.local')
  )
}

export function isWysehubPortalHostname(hostname: string): boolean {
  const host = normalizeManagedHostname(hostname)
  return host.startsWith('portal.') && host.includes('wysehub')
}

export function isWysehubAdminHostname(hostname: string): boolean {
  const host = normalizeManagedHostname(hostname)
  return host.startsWith('admin.') && host.includes('wysehub')
}

export function isStagingManagedHost(hostname: string): boolean {
  return normalizeManagedHostname(hostname).includes('staging')
}

/** AiClaims portal host for the same environment as the current request. */
export function inferAiClaimsPortalHostname(currentHost: string): string {
  const host = normalizeManagedHostname(currentHost)
  if (isAiClaimsPortalHostname(host)) {
    return host
  }
  if (isAiClaimsRootHostname(host)) {
    return `portal.${host}`
  }
  return (
    parseHostnameFromEnv(process.env.NEXT_PUBLIC_AICLAIMS_PORTAL_HOST) ||
    (isStagingManagedHost(host)
      ? 'portal.staging.aiclaims.co.uk'
      : 'portal.aiclaims.co.uk')
  )
}

/** WyseHub portal host for the same environment as the current request. */
export function inferWysehubPortalHostname(currentHost: string): string {
  const host = normalizeManagedHostname(currentHost)
  if (isWysehubPortalHostname(host)) {
    return host
  }
  if (isWysehubHostname(host) || isAiClaimsHostname(host)) {
    return `portal.${inferWysehubApexHostname(host)}`
  }
  return (
    parseHostnameFromEnv(process.env.NEXT_PUBLIC_WYSEHUB_PORTAL_HOST) ||
    `portal.${inferWysehubApexHostname(host)}`
  )
}

/** WyseHub admin host for the same environment as the current request. */
export function inferWysehubAdminHostname(currentHost: string): string {
  const host = normalizeManagedHostname(currentHost)
  if (isWysehubAdminHostname(host)) {
    return host
  }
  if (isWysehubHostname(host) || isAiClaimsHostname(host)) {
    return `admin.${inferWysehubApexHostname(host)}`
  }
  return (
    parseHostnameFromEnv(process.env.NEXT_PUBLIC_WYSEHUB_ADMIN_HOST) ||
    `admin.${inferWysehubApexHostname(host)}`
  )
}

/**
 * WyseHub apex host for the same environment as the current request
 * (wysehub.co.uk, staging.wysehub.co.uk, martin.dev.wysehub.co.uk, …).
 * On AiClaims hosts the environment is mirrored across brands, so
 * portal.staging.aiclaims.co.uk maps to staging.wysehub.co.uk.
 */
export function inferWysehubApexHostname(currentHost: string): string {
  const host = normalizeManagedHostname(currentHost).replace(
    /^(portal|admin)\./,
    '',
  )
  if (isWysehubHostname(host)) {
    return host
  }
  if (isAiClaimsHostname(host)) {
    return host
      .replace(/(^|\.)aiclaims\.co\.uk$/, '$1wysehub.co.uk')
      .replace(/(^|\.)aiclaims\.local$/, '$1wysehub.local')
  }
  return (
    parseHostnameFromEnv(process.env.NEXT_PUBLIC_API_URL) ||
    (isStagingManagedHost(host) ? 'staging.wysehub.co.uk' : 'wysehub.co.uk')
  )
}

export function inferAiClaimsRootHostname(currentHost: string): string {
  const host = normalizeManagedHostname(currentHost)
  if (isAiClaimsRootHostname(host)) {
    return host
  }
  if (isAiClaimsPortalHostname(host)) {
    return host.slice('portal.'.length)
  }
  return (
    parseHostnameFromEnv(process.env.NEXT_PUBLIC_AICLAIMS_HOST) ||
    (isStagingManagedHost(host) ? 'staging.aiclaims.co.uk' : 'aiclaims.co.uk')
  )
}

export function buildAiClaimsPortalLoginUrl(
  currentHost: string,
  protocol = 'https:',
  nextPath = '/dashboard',
): string {
  const portalHost = inferAiClaimsPortalHostname(currentHost)
  const params = new URLSearchParams({
    login: 'true',
    next: nextPath,
  })
  return `${protocol}//${portalHost}/?${params.toString()}`
}
